import { IManagePlaceOrderReportsTo, IUserRole } from '.';

export interface IUser {
  userName: string;
  userRoles: string[];
  userAgreementCompleted: boolean;
  placeOrder: {
    placeOrderRole: IUserRole;
    reportsTo: IManagePlaceOrderReportsTo;
  };
  reportsTo: string | null;
  territoryIds: string[];
  neighborhoodIds: string[];
  externalUser?: IExternalUser;
  ldFlagsInitialized?: boolean;
  fullName: string | null;
  isAdditionalUser?: boolean;
  impersonatedByFullName: string;
  isMember: boolean;
  jobTitle?: string | null;
}

export interface IDomainUserRes {
  records: IDomainUser[];
  totalRecordCount: number;
}

export interface IDomainUser {
  accountName: string;
  displayName: string;
  isActive: boolean;
  distinguishedName: string;
  email: string;
  entraUserId?: string;
  placeOrderRole: IUserRole;
  reportsTo: IManagePlaceOrderReportsTo;
  userType: UserTypes;
}

export interface IExternalUserPartner {
  companyId: number;
  companySageId: number;
  name: string;
  subType: string | null;
  type: string;
}

export interface IExternalUser {
  firstName: string;
  lastName: string;
  personId: number;
  partners: IExternalUserPartner[];
  buildIds?: number[];
  latestBuildId?: number;
  latestBuildPlanStyle: string;
  vendorUserManagement?: number[];
}

export interface IAllUser {
  displayName: string;
  userName: string;
}

export interface IEntraInformation {
  entraUserId: string;
  roleIds: string[];
}
export interface IPlaceOrderRoleInformation {
  displayName: string;
  accountName: string;
  roleId: number;
  reportsTo: string;
}
export interface IUpdateUserRoles {
  entraRoleInformation: IEntraInformation;
  placeOrderRoleInformation: IPlaceOrderRoleInformation;
}

export interface IUserAppRoles {
  roles: string[];
  canImpersonateExternalUser: boolean;
  canImpersonateInternalUser: boolean;
}

export interface IUserAppRoleDescription {
  name: string;
  description: string;
}

export enum UserTypes {
  Member = 'Member',
  Guest = 'Guest'
}

export enum UserRoles {
  BillingAdmin = 'Billing Admin',
  FinancialAdmin = 'Financial Admin',
  BLActivityTracking = 'BL_ActivityTracking',
  BLAdmin = 'BL_Admin',
  BLAgent = 'BL_Agent',
  BLAttorney = 'BL_Attorney',
  BLAssistSuper = 'BL_AssistSuper',
  BLClient = 'BL_Client',
  BLClosing = 'BL_Closing',
  BLCompaction = 'BL_Compaction',
  BLDeleteBuild = 'BL_DeleteBuild',
  BLDevelopment = 'BL_Development',
  BLField = 'BL_Field',
  BLFieldAdmin = 'BL_FieldAdmin',
  BLFieldUserAdmin = 'BL_FieldUserAdmin',
  BLFinance = 'BL_Finance',
  BLProjectManager = 'BL_ProjectManager',
  BLProjectmanager1 = 'BL_Projectmanager1',
  BLProjectmanager2 = 'BL_Projectmanager2',
  BLProjectmanager3 = 'BL_Projectmanager3',
  BLProjectmanager4 = 'BL_Projectmanager4',
  BLReporting = 'BL_Reporting',
  BLSales = 'BL_Sales',
  BLSalesAdmin = 'BL_SalesAdmin',
  BLSalesCoordinator = 'BL_SalesCoordinator',
  BLStartSheetTemplates = 'BL_StartSheetTemplates',
  BLSubcontractor = 'BL_Subcontractor',
  BLSuperintendent1 = 'BL_Superintendent1',
  BLSuperintendent2 = 'BL_Superintendent2',
  BLSuperintendent3 = 'BL_Superintendent3',
  BLSuperintendent4 = 'BL_Superintendent4',
  BLUserAdmin = 'BL_UserAdmin',
  BLVendor = 'BL_Vendor',
  BLWarranty = 'BL_Warranty',
  BLLender = 'BL_Lender',
  ContentManager = 'Content Manager',
  GlobalAdmin = 'Global Admin',
  VendorContractsManagement = 'VendorContractsManagement',
  VendorInvoiceManagement = 'VendorInvoiceManagement',
  VendorPurchaseOrderManagement = 'VendorPurchaseOrderManagement',
  VendorPunchlistManager = 'VendorPunchlistManager',
  VendorUserManagement = 'VendorUserManagement',
  VendorCompactionManager = 'VendorCompactionManager',
  WarrantyAdmin = 'Warranty Admin',
  BLCreateEntraUser = 'BL_Create_Entra_User',
  BLImpersonationInternal = 'BL_Impersonation_Internal',
  BLImpersonationExternal = 'BL_Impersonation_External',
  BLFinancialTools = 'BL_Financial_Tools'
}

export const PunchlistGeneralAssignmentUserId = '8ADC6725-E8E4-4A49-8409-EBDDD6763857';

export interface IJobTitle {
  allowsNeighborhoodConfiguration: boolean;
  allowsTerritoryConfiguration: boolean;
  description: string;
  isExternal: boolean;
  requiresPlaceOrderReportsTo: boolean;
  value: number;
  personTypeName: string;
}

export interface ICreateUser {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: number | null;
  roleIds: string[];
  internalUser?: {
    reportsTo?: string;
    territoryIds?: number[];
    neighborhoodIds?: number[];
  };
}

export interface IUpdateUser extends ICreateUser {
  internalUser?: {
    reportsTo?: string;
    territoryIds?: number[];
    neighborhoodIds?: number[];
    allowedAccountNames?: string[];
    sageEmployeeId?: string;
  };
  externalUser?: {
    phoneNumber: string;
    cellPhoneNumber: string;
    faxNumber: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    isActive: boolean;
    agent?: {
      workplace: string;
      webSite: string;
      includeAgentNameInUrl: boolean;
      neighborhoodIds: number[];
    };
    attorney?: {
      attorneyOfficeId: number;
    };
    purchaser?: {
      agentId: number;
      contractName: string;
      buyerHasAgent: boolean;
      externalAgentId: number;
      agentFirstName: string;
      agentLastName: string;
      agentEmail: string;
      agentPhone: string;
      agentSellingCompany: string;
      hideBuilds: boolean;
    };
    vendor?: {
      partners: {
        partnerId: number;
        roles: string[];
      }[];
    };
    lender?: {
      workplace: string;
      lenderStatus: string;
      lenderTerritoryIds: number[];
    };
    lenderProcessor?: {
      workplace: string;
      lenderStatus: string;
      lenderTerritoryIds: number[];
      assignedLenders: number[];
    };
    custom?: {
      workplace: string;
    };
  };
}

export interface IAdminUser {
  userId: number;
  entraUserId: string;
  personId: number | null;
  firstName: string;
  lastName: string;
  displayName: string;
  accountName: string;
  email: string;
  jobTitle: number | null;
  jobTitlePersonType: string | null;
  roleIds: string[];
  userType: string;
  requiresPlaceOrderReportsTo: boolean;
  isMember: boolean;
  internalUser?: {
    placeOrderRoleId: string;
    placeOrderReportsTo: string;
    allowedAccountNames: string[];
    neighborhoods:
      | {
          neighborhoodId: number;
          name: string;
          phase: string;
        }[]
      | null;
    territories:
      | {
          territoryPersonnelId: number;
          territoryId: number;
          personnelType: string;
          name: string;
        }[]
      | null;
    canImpersonateExternalUser: boolean;
    canImpersonateInternalUser: boolean;
    sageEmployeeRecnum: string;
    sageEmployeeDisplayInfo: string | null;
  };
  externalUser?: {
    isActive: boolean;
    phoneNumber: string;
    cellPhoneNumber: string;
    faxNumber: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    agent?: {
      workplace: string;
      webSite: string;
      includeAgentNameInUrl: boolean;
      neighborhoodIds: number[];
    };
    attorney?: {
      attorneyOfficeId: number;
    };
    purchaser?: {
      agentId: number;
      contractName: string;
      buyerHasAgent: boolean;
      externalAgentId: number;
      agentFirstName: string;
      agentLastName: string;
      agentEmail: string;
      agentPhone: string;
      agentSellingCompany: string;
      hideBuilds: boolean;
      additionalContacts: {
        name: string;
        email: string;
        phoneNumber: string;
      }[];
    };
    vendor?: {
      partners: {
        partnerId: number;
        roles: string[];
      }[];
    };
    lender?: {
      workplace: string;
      lenderStatus: string;
      lenderTerritoryIds: number[];
    };
    lenderProcessor?: {
      workplace: string;
      lenderStatus: string;
      lenderTerritoryIds: number[];
      assignedLenders: number[];
    };
    custom?: {
      workplace: string;
    };
  };
}

export interface IUserWithAccess {
  userId: number;
  accountName: string;
  displayName: string;
  distinguishedName: string;
  email: string;
  entraUserId: string;
  loginFailedCount: number;
  placeOrderRole: {
    placeOrderRoleId: number;
    name: string;
    spendLimit: number;
    spendLimitChangeOrder: number;
  };
  reportsTo: {
    accountName: string;
    displayName: string;
  };
  userType: UserTypes;
}

export interface IUnifiedUser {
  userId: number;
  name: string;
  email: string;
  jobTitle: string;
}

export interface ISageEmployee {
  value: string;
  description: string;
  shorthand: string;
}

export interface IUserByEmail {
  userId: number;
  personId: number;
  accountIsDisabled: boolean;
}

export enum JobTitles {
  AccountingAssistant = 'AccountingAssistant',
  AssistantController = 'AssistantController',
  BillingAdmin = 'BillingAdmin',
  CEO = 'CEO',
  CLO = 'CLO',
  ContentManager = 'ContentManager',
  Controller = 'Controller',
  DirectorOfArchitecture = 'DirectorOfArchitecture',
  DirectorOfLandManagement = 'DirectorOfLandManagement',
  DirectorOfMarketing = 'DirectorOfMarketing',
  DivisionPresidentNorth = 'DivisionPresidentNorth',
  DivisionPresidentSouth = 'DivisionPresidentSouth',
  Draftsman = 'Draftsman',
  Estimator = 'Estimator',
  FacilitiesMaintenance = 'FacilitiesMaintenance',
  FieldAssistant = 'FieldAssistant',
  FinancialAdmin = 'FinancialAdmin',
  ForemanNorth = 'ForemanNorth',
  ForemanSouth = 'ForemanSouth',
  GlobalAdmin = 'GlobalAdmin',
  GraphicDesigner = 'GraphicDesigner',
  IT = 'IT',
  MarketManager = 'MarketManager',
  PayrollManager = 'PayrollManager',
  PermitSpecialist = 'PermitSpecialist',
  ProjectManagerNorth = 'ProjectManagerNorth',
  ProjectManagerSouth = 'ProjectManagerSouth',
  Receptionist = 'Receptionist',
  SalesAdmin = 'SalesAdmin',
  SalesAssistant = 'SalesAssistant',
  Shareholder = 'Shareholder',
  SuperintendentNorth = 'SuperintendentNorth',
  SuperintendentSouth = 'SuperintendentSouth',
  WarrantySuperintendent = 'WarrantySuperintendent',
  WarrantyAdmin = 'WarrantyAdmin',
  WarrantyManager = 'WarrantyManager'
}
